import MainParticipantInfo from './MainParticipantInfo';
import ParticipantTracks from './ParticipantTracks';
import React, { useState } from 'react';
import useMainSpeaker from '../hooks/useMainSpeaker';
import useSelectedParticipant from './VideoProvider/useSelectedParticipant';
import Avatar from './Avatar';
import ParticipantNetworkQualityLevel, { useParticipantNetworkQualityLevel } from '../hooks/useParticipantNetworkQualityLevel';
import useVideoContext from '../hooks/useVideoContext';
import MiniSpinner from '../../../common/MiniSpinner';
import NetworkQualityLevel from '../components/NetworkQualityLevel';
import BandwidthWarning from '../components/BandwidthWarning';
import useScreenShareParticipant from '../hooks/useScreenShareParticipant';
import useIsTrackEnabled from '../hooks/useIsTrackEnabled';
import usePublications from '../hooks/usePublications';
import useTrack from '../hooks/useTrack';
import useIsTrackSwitchedOff from '../hooks/useIsTrackSwitchedOff';
import useIsTrackContentPreferences from '../hooks/useIsTrackContentPreferences';
import MainParticipantOverlay from './MainParticipantOverlay';
import { PROVIDER } from '../constants';
import { useTwilioAppState } from '../state';
import YesNoComponent from './YesNoComponent';

const MainParticipant = (props) => {
    const mainParticipant = useMainSpeaker();
    const [selectedParticipant] = useSelectedParticipant();

    const screenShareParticipant = useScreenShareParticipant();
    const { room: { localParticipant }, kickoutGuest } = useVideoContext();

    const [isNQWarningClosed, setNQWarningClosed] = useState(false);
    const showReconnecting = mainParticipant && mainParticipant !== localParticipant && mainParticipant.state && mainParticipant.state === "reconnecting" ? true : false;
    //const mainParticipantNQL = useParticipantNetworkQualityLevel(mainParticipant, mainParticipant === localParticipant ? true : false);
    let localParticipantNQL = useParticipantNetworkQualityLevel(localParticipant, true);

    let showRemoteParticipantNQL = false;
    const [remoteParticipantNQL, setRemoteParticipantNQL] = useState();
    const [showParticipantOverlay, setShowParticipantOverlay] = useState(false);
    const { source } = useTwilioAppState();
    const showTwilioNetworkBanner = window.serverData.data.showTwilioNetworkBanner;
    const showTwilioNetworkIndicator = window.serverData.data.showTwilioNetworkIndicator;
    const abilityToTurnoffVideoorAudioForParticipants = window.serverData.data.abilityToTurnoffVideoorAudioForParticipants;
    const [showRemoveGuestModal, setShowRemoveGuestModal] = useState(false);
    if (mainParticipant !== localParticipant && remoteParticipantNQL && remoteParticipantNQL !== 0)
        showRemoteParticipantNQL = true;

    const identityArray = mainParticipant && mainParticipant.identity && mainParticipant.identity.split("||");
    const emailOrPhone = identityArray && identityArray.length >= 1 && identityArray[1] || "";
    const participantName = identityArray && identityArray.length >= 3 && identityArray[3] || "";
    const participantType = identityArray && identityArray.length >= 2 && identityArray[2] || "";

    const videoPriority = mainParticipant === selectedParticipant || mainParticipant === screenShareParticipant ? 'high' : null;
    const publications = usePublications(mainParticipant);
    const audioPublication = publications.find(p => p.kind === 'audio');
    const audioTrack = useTrack(audioPublication);
    const isTrackEnabled = useIsTrackEnabled(audioTrack);

    const videoPublication = publications.find(p => p.trackName.includes('camera'));
    const screenSharePublication = publications.find(p => p.trackName.includes('screen'));

    const videoTrack = useTrack(screenSharePublication || videoPublication);
    const isVideoEnabled = Boolean(videoTrack);
    const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack);
    const toggle = () => setShowParticipantOverlay(!showParticipantOverlay);
    const removeGuestModalText = `<h3>Are you sure you want to remove <span class="kick-out-name">${participantName}</span>?</h3>`;
    const isTrackContentPreferences = useIsTrackContentPreferences(videoTrack);

    const onYesClick = () => {
        setShowRemoveGuestModal(false);
        if (source === PROVIDER) {
            kickoutGuest(mainParticipant.identity);
        }
    }

    const onNoClick = () => {
        setShowRemoveGuestModal(false);
    }

    let render;
    const children = <React.Fragment>
        {showTwilioNetworkBanner && localParticipantNQL && localParticipantNQL !== 0 && localParticipantNQL === 1 && !isNQWarningClosed && <BandwidthWarning isNQWarningClosed={isNQWarningClosed} setNQWarningClosed={setNQWarningClosed} />}
        {mainParticipant !== localParticipant && <ParticipantNetworkQualityLevel setState={setRemoteParticipantNQL} participant={mainParticipant} isLocalParticipant={false} />}
        {source === PROVIDER ?
            <React.Fragment>{mainParticipant !== localParticipant &&
                <div className="main-info">
                    <span className="participant-name remote-participant-name">
                        {showTwilioNetworkIndicator && showRemoteParticipantNQL && <NetworkQualityLevel qualityLevel={remoteParticipantNQL} />}
                        <div className="participant-name-label">{participantName}</div>
                        {!isTrackEnabled && <span className="icon-mic-off" />}
                        {abilityToTurnoffVideoorAudioForParticipants && <span onClick={toggle} className="icon-dots" id="dotsIcon" />}
                    </span>
                </div>
            } </React.Fragment>
            : <React.Fragment>{mainParticipant !== localParticipant &&
                <div className="main-info">
                    <span className="participant-name">
                        {showTwilioNetworkIndicator && showRemoteParticipantNQL && <NetworkQualityLevel qualityLevel={remoteParticipantNQL} />}
                        <div className="participant-name-label">{participantName}</div>{!isTrackEnabled && <span className="icon-mic-off" />}
                    </span>
                </div>
            } </React.Fragment>
        }
        <ParticipantTracks participant={mainParticipant} videoPriority={videoPriority} enableScreenShare />
        {mainParticipant === localParticipant && <div className="main-info"><span className="participant-name"> {showTwilioNetworkIndicator && <NetworkQualityLevel qualityLevel={localParticipantNQL} />}<div className="participant-name-label">{participantName}</div>{!isTrackEnabled && <span className="icon-mic-off" />}</span></div>}
        {(!isVideoEnabled || isVideoSwitchedOff) && <Avatar userName={participantName} isLocalParticipant={mainParticipant === localParticipant} />}
        {abilityToTurnoffVideoorAudioForParticipants && mainParticipant !== localParticipant && showParticipantOverlay &&
            <MainParticipantOverlay appointmentDetails={props.appointmentDetails} identity={mainParticipant.identity} participantName={participantName} participantType={participantType} emailOrPhone={emailOrPhone} toggle={toggle} isAudioEnabled={isTrackEnabled} isVideoEnabled={isVideoEnabled} target="dotsIcon" setShowRemoveGuestModal={setShowRemoveGuestModal} />
        }
        <YesNoComponent
            showModal={showRemoveGuestModal}
            className="kick-out-modal"
            modalText={removeGuestModalText}
            modalYesFunction={onYesClick}
            modalNoFunction={onNoClick}
        />
    </React.Fragment>;

    if (showReconnecting) {
        render = (<div className="row token-fetching-loader">
            <div className="col-12 align-self-center">
                <div className="msg-reconnecting">Something went wrong. We're trying to rejoin the call.</div>
                <MiniSpinner className="videovisit-mini-spinner" />
            </div>
        </div>);
    }

    else {
        render = (<MainParticipantInfo participant={mainParticipant}>
            {children}
        </MainParticipantInfo>);
    }

    return (render);
}

export default MainParticipant;